import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { COUPLE } from "@/lib/constants";
import { useReveal } from "@/contexts/RevealContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/story", label: "Our Story" },
  { to: "/gallery", label: "Gallery" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { revealed } = useReveal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-ink-900/75 border-b border-gold/20"
          : "backdrop-blur-md bg-ink-900/30 border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <NavLink to="/" data-testid="nav-logo" className="group flex items-center gap-2">
          <span className="font-serif italic text-xl md:text-2xl gold-text tracking-wide">
            P <span className="text-ivory/70 not-italic mx-0.5">&amp;</span> A
          </span>
          {revealed && (
            <span
              data-testid="navbar-date"
              className="hidden md:inline-block text-[0.6rem] uppercase tracking-[0.4em] text-ivory-dim border-l border-gold/30 pl-3 ml-2 animate-fade-in"
            >
              {COUPLE.weddingDateLabel}
            </span>
          )}
        </NavLink>

        <ul className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative px-5 py-2 text-[0.72rem] uppercase tracking-[0.32em] font-medium transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-ivory-muted hover:text-ivory"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={`absolute left-1/2 -bottom-0.5 h-px bg-gold transition-all duration-500 ${
                        isActive ? "w-6 -translate-x-1/2 opacity-100" : "w-0 opacity-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        <span
          data-testid="navbar-hashtag"
          className="hidden md:inline-block font-serif italic text-lg gold-text tracking-wider"
        >
          #PandA
        </span>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-gold/30 text-gold hover:bg-burgundy/40 transition"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </nav>

      {/* Mobile dropdown */}
      <div
        data-testid="nav-mobile-menu"
        className={`md:hidden overflow-hidden transition-all duration-500 backdrop-blur-xl bg-ink-900/90 border-b border-gold/20 ${
          open ? "max-h-96" : "max-h-0"
        }`}
      >
        <ul className="px-6 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <li key={l.to}>
              <NavLink
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block py-3 px-2 text-sm uppercase tracking-[0.32em] ${
                    isActive ? "text-gold" : "text-ivory-muted"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
          <li className="pt-2 pb-1 font-serif italic text-base gold-text">#PandA</li>
        </ul>
      </div>
    </header>
  );
}
